/**
 * Eventos derivados no cliente (§14.8.8): função pura e determinística. Mesmos snapshots → mesmos eventos, mesmos ids,
 * em qualquer máquina. Nenhuma regra usa relógio (nem o da fonte nem o do aparelho): só campos dos snapshots e das
 * colunas municipais. O publicador roda a mesma função sobre a série completa (PE-09) e grava `events.json`; aqui os
 * eventos são PROVISÓRIOS até o `events.json` válido que os cobre (`mergeEvents`).
 *
 * Não derivados no cliente: `results_open`, `tse_stalled` e `tse_resumed` (só o publicador emite).
 *
 * Leituras de lacunas (registradas): `memo.lastLeader` é o último líder CONFIRMADO (o de `prev1` mantido em `curr`),
 * como no simulador (o publicador da simulação); um só `count_milestone` por seq (o maior marco cruzado), porque o id
 * `${type}:BR:${seq}` não distingue dois marcos no mesmo seq; correção no Exterior com `scope.level = 'ABROAD'`.
 */
import {
  type CandidateNumber,
  type IbgeCode,
  type Iso,
  PLACE_CODES,
  type PlaceCode,
  tMin,
  UF_CODES,
  type UfCode,
} from '../contracts/base';
import { type ElectionEvent, type ElectionEventData, eventId } from '../contracts/events';
import type { HistoryPoint } from '../contracts/history';
import type { MunicipalColumns } from '../contracts/municipal';
import { type ProbabilityPayload, PUBLIC_REASONS } from '../contracts/probability';
import type { Snapshot } from '../contracts/snapshot';
import type { Geography } from '../contracts/static';
import type { PlaceResult } from '../contracts/tally';

export interface EventRulesConfig {
  leadChangeMinFraction: number;
  ufLeadChangeMinFraction: number;
  ufLeadChangeCooldownMin: number;
  milestones: readonly number[];
  tseStalledAfterMin: number;
  batchMinPlaceDelta: number;
  abroadFirstCities: number;
  /** 27 códigos IBGE das capitais (inventário 07 E.1). */
  capitals: readonly IbgeCode[];
  /** Capital com o mesmo nome da UF (texto de §13.5). */
  capitalSameNameUf: readonly UfCode[];
  /** Brasília coincide com a UF. */
  capitalSkipUf: readonly UfCode[];
}

/** Capitais das 27 UFs (código IBGE de 7 dígitos). */
export const CAPITALS_IBGE: Readonly<Record<UfCode, IbgeCode>> = Object.freeze({
  AC: '1200401',
  AL: '2704302',
  AM: '1302603',
  AP: '1600303',
  BA: '2927408',
  CE: '2304400',
  DF: '5300108',
  ES: '3205309',
  GO: '5208707',
  MA: '2111300',
  MG: '3106200',
  MS: '5002704',
  MT: '5103403',
  PA: '1501402',
  PB: '2507507',
  PE: '2611606',
  PI: '2211001',
  PR: '4106902',
  RJ: '3304557',
  RN: '2408102',
  RO: '1100205',
  RR: '1400100',
  RS: '4314902',
  SC: '4205407',
  SE: '2800308',
  SP: '3550308',
  TO: '1721000',
});

export const EVENT_RULES: EventRulesConfig = Object.freeze({
  leadChangeMinFraction: 0.05,
  ufLeadChangeMinFraction: 0.1,
  ufLeadChangeCooldownMin: 10,
  milestones: Object.freeze([0.01, 0.05, 0.1, 0.25, 0.5, 0.75, 0.9, 0.99]),
  tseStalledAfterMin: 5,
  batchMinPlaceDelta: 1,
  abroadFirstCities: 3,
  capitals: Object.freeze(UF_CODES.map((uf) => CAPITALS_IBGE[uf])),
  capitalSameNameUf: Object.freeze(['SP', 'RJ'] as UfCode[]),
  capitalSkipUf: Object.freeze(['DF'] as UfCode[]),
});

/** Estado mínimo entre chamadas. */
export interface DeriveMemo {
  /** Último líder confirmado (não nulo) por lugar. */
  lastLeader: Partial<Record<'BR' | PlaceCode, CandidateNumber>>;
  /** t_min da última mudança de liderança emitida por UF. */
  lastUfChangeTMin: Partial<Record<UfCode, number>>;
  /** Extensão: o modelo já esteve ativo (distingue `model_resumed` de `model_activated`). */
  modelActivated?: boolean;
}

export const EMPTY_MEMO: DeriveMemo = Object.freeze({ lastLeader: {}, lastUfChangeTMin: {} });

export interface DeriveColumns {
  prev: MunicipalColumns | null;
  curr: MunicipalColumns | null;
}

type Pair = readonly [CandidateNumber, CandidateNumber];
type Scope = ElectionEvent['scope'];

const round6 = (v: number) => Math.round(v * 1e6) / 1e6;
const fracOf = (p: Pick<PlaceResult, 'sections' | 'sections_counted'>) =>
  p.sections > 0 ? p.sections_counted / p.sections : 0;
const votesOf = (p: PlaceResult, pair: Pair): [number, number] => [p.votes[pair[0]] ?? 0, p.votes[pair[1]] ?? 0];
const sharesOfVotes = (va: number, vb: number): [number, number] => {
  const v = va + vb;
  return v > 0 ? [round6(va / v), round6(vb / v)] : [0, 0];
};
const marginPpOf = (va: number, vb: number) => {
  const v = va + vb;
  return v > 0 ? Math.round((Math.abs(vb - va) / v) * 1e6) / 1e4 : 0;
};
const leaderOfVotes = (va: number, vb: number, pair: Pair): CandidateNumber | null =>
  va > vb ? pair[0] : vb > va ? pair[1] : null;
const statusOf = (p: PlaceResult) => p.status;

function summaryOf(va: number, vb: number, pair: Pair) {
  return { leader: leaderOfVotes(va, vb, pair), shares: sharesOfVotes(va, vb), margin_pp: marginPpOf(va, vb) };
}

function makeEvent(
  type: ElectionEvent['type'],
  seq: number,
  occurredAt: Iso,
  detectedAt: Iso,
  scope: Scope,
  salience: 1 | 2 | 3,
  nature: ElectionEvent['nature'],
  office: ElectionEvent['office'],
  data: ElectionEventData,
): ElectionEvent {
  return {
    id: eventId(type, scope.code, seq),
    type,
    office,
    seq,
    occurred_at: occurredAt,
    detected_at: detectedAt,
    scope,
    salience,
    nature,
    data,
  };
}

/** public_reason (catálogo fechado, P-04) → `reason` do evento de modelo. */
export function modelEventReason(publicReason: string | null): 'fonte_sem_novidade' | 'decisao_editorial' | null {
  if (publicReason === PUBLIC_REASONS[2]) return 'fonte_sem_novidade';
  if (publicReason === PUBLIC_REASONS[3]) return 'decisao_editorial';
  return null;
}

/** Fração de seções (ou de aptos, sem a coluna) de uma linha das colunas, 0–10000; −1 sem informação. */
function columnFraction(block: MunicipalColumns['brazil'], i: number): number {
  const s = block.sections_fraction?.[i];
  return s !== undefined && s >= 0 ? s : (block.electorate_fraction[i] ?? -1);
}

/** Cidades do exterior com votos nas colunas, até `max`, por eleitorado decrescente (e ordem do cadastro). */
function abroadCitiesWithVotes(cols: MunicipalColumns | null, geo: Geography | null, max: number): string[] {
  if (!cols || !geo) return [];
  const block = cols.abroad;
  const out: Array<{ idx: number; e: number; tse: string }> = [];
  geo.abroad_cities.forEach((c, idx) => {
    const votes = (block.votes_a[idx] ?? 0) + (block.votes_b[idx] ?? 0);
    if (columnFraction(block, idx) > 0 || votes > 0) out.push({ idx, e: c.electorate_ref, tse: c.tse });
  });
  return out
    .sort((x, y) => y.e - x.e || x.idx - y.idx)
    .slice(0, max)
    .map((x) => x.tse);
}

/** Interpolação linear entre prev2 e prev1 no ponto em que as fatias se igualam (§14.8.8). */
function crossingOf(p2: PlaceResult | null, p1: PlaceResult, to: CandidateNumber, pair: Pair) {
  if (!p2) return undefined;
  const [a2, b2] = votesOf(p2, pair);
  if (a2 + b2 <= 0) return undefined;
  const k = to === pair[0] ? 0 : 1;
  const s2 = sharesOfVotes(a2, b2)[k];
  const s1 = sharesOfVotes(...votesOf(p1, pair))[k];
  if (!(s2 < 0.5 && s1 >= 0.5 && s1 > s2)) return undefined;
  const g2 = fracOf(p2);
  const g1 = fracOf(p1);
  return { sections_fraction: round6(g2 + ((0.5 - s2) / (s1 - s2)) * (g1 - g2)), share_to: 0.5 };
}

function cloneMemo(m: DeriveMemo): DeriveMemo {
  const out: DeriveMemo = { lastLeader: { ...m.lastLeader }, lastUfChangeTMin: { ...m.lastUfChangeTMin } };
  if (m.modelActivated !== undefined) out.modelActivated = m.modelActivated;
  return out;
}

/** Mesmos snapshots → mesmos eventos, mesmos ids (AC-14.8-07). Só snapshots com seq novo entram no trio. */
export function deriveEvents(
  prev2: Snapshot | null,
  prev1: Snapshot | null,
  curr: Snapshot,
  geo: Geography | null,
  cfg: EventRulesConfig,
  memoIn: DeriveMemo,
  cols: DeriveColumns,
): { events: ElectionEvent[]; memo: DeriveMemo } {
  const memo = cloneMemo(memoIn);
  if (!prev1) return { events: [], memo };
  const out: ElectionEvent[] = [];
  const date = curr.election.date;
  const pair = curr.races.president.candidates as Pair;
  const seq = curr.seq;
  const occurred = curr.clocks.tse_ht ?? curr.clocks.published_at;
  const detected = curr.clocks.published_at;
  const P1 = prev1.races.president;
  const C = curr.races.president;
  const P2 = prev2?.races.president ?? null;
  const official = (type: ElectionEvent['type'], scope: Scope, salience: 1 | 2 | 3, data: ElectionEventData) =>
    out.push(makeEvent(type, seq, occurred, detected, scope, salience, 'official', 'president', data));

  // ── Exterior ──
  const zz = { level: 'ABROAD' as const, code: 'ZZ' };
  const zzPrev = P1.places.ZZ;
  const zzCurr = C.places.ZZ;
  if (zzPrev && zzCurr) {
    if (zzPrev.sections_counted === 0 && zzCurr.sections_counted > 0) {
      official('abroad_first_votes', zz, 2, {
        type: 'abroad_first_votes',
        cities: abroadCitiesWithVotes(cols.curr, geo, cfg.abroadFirstCities),
      });
    }
    if (fracOf(zzPrev) < 0.5 && fracOf(zzCurr) >= 0.5) {
      official('abroad_half', zz, 2, { type: 'abroad_half', ...summaryOf(...votesOf(zzCurr, pair), pair) });
    }
    if (statusOf(zzPrev) !== 'complete' && statusOf(zzCurr) === 'complete') {
      official('abroad_complete', zz, 2, { type: 'abroad_complete', ...summaryOf(...votesOf(zzCurr, pair), pair) });
    }
  }

  // ── UFs: primeiros votos e conclusão ──
  const firstUfs = UF_CODES.filter(
    (uf) => (P1.places[uf]?.sections_counted ?? 0) === 0 && (C.places[uf]?.sections_counted ?? 0) > 0,
  );
  if (firstUfs.length > 0) {
    const scope: Scope = firstUfs.length === 1 ? { level: 'UF', code: firstUfs[0] as string } : { level: 'BR' };
    official('uf_first_votes', scope, 2, { type: 'uf_first_votes', places: [...firstUfs] });
  }
  for (const uf of UF_CODES) {
    const before = P1.places[uf];
    const after = C.places[uf];
    if (before && after && statusOf(before) !== 'complete' && statusOf(after) === 'complete') {
      official('uf_complete', { level: 'UF', code: uf }, 2, {
        type: 'uf_complete',
        ...summaryOf(...votesOf(after, pair), pair),
      });
    }
  }

  // ── Capitais (só com colunas) ──
  if (geo && cols.prev && cols.curr && cols.curr !== cols.prev) {
    const block0 = cols.prev.brazil;
    const block1 = cols.curr.brazil;
    const capitals = new Set(cfg.capitals);
    const colsSeq = cols.curr.snapshot_seq;
    const colsOccurred = cols.curr.clocks.tse_ht ?? occurred;
    geo.municipalities.forEach((m, idx) => {
      if (!capitals.has(m.ibge) || cfg.capitalSkipUf.includes(m.uf)) return;
      if (columnFraction(block0, idx) === 10000 || columnFraction(block1, idx) !== 10000) return;
      const va = block1.votes_a[idx] ?? 0;
      const vb = block1.votes_b[idx] ?? 0;
      out.push(
        makeEvent(
          'capital_complete',
          colsSeq,
          colsOccurred,
          detected,
          { level: 'MUN', code: m.ibge },
          2,
          'official',
          'president',
          {
            type: 'capital_complete',
            ...summaryOf(va, vb, pair),
            ibge: m.ibge,
          },
        ),
      );
    });
  }

  // ── Lotes e marcos ──
  const deltas = PLACE_CODES.map((code) => ({
    code,
    d: Math.max(0, (C.places[code]?.sections_counted ?? 0) - (P1.places[code]?.sections_counted ?? 0)),
  }));
  const total = deltas.reduce((s, x) => s + x.d, 0);
  if (total >= 1) {
    official('sections_batch', { level: 'BR' }, 1, {
      type: 'sections_batch',
      sections: total,
      places: deltas
        .filter((x) => x.d >= cfg.batchMinPlaceDelta)
        .sort((x, y) => y.d - x.d || (x.code < y.code ? -1 : 1))
        .map((x) => x.code),
      shares: sharesOfVotes(...votesOf(C.national, pair)),
      national_sections_fraction: round6(fracOf(C.national)),
    });
  }
  const f0 = fracOf(P1.national);
  const f1 = fracOf(C.national);
  const crossed = cfg.milestones.filter((m) => f0 < m && m <= f1);
  const milestone = crossed[crossed.length - 1];
  if (milestone !== undefined) {
    official('count_milestone', { level: 'BR' }, 1, {
      type: 'count_milestone',
      milestone: milestone as Extract<ElectionEventData, { type: 'count_milestone' }>['milestone'],
      shares: sharesOfVotes(...votesOf(C.national, pair)),
    });
  }

  // ── Mudanças de liderança confirmadas (líder de prev1 mantido em curr, diferente do último confirmado) ──
  const prev1Ht = prev1.clocks.tse_ht;
  const confirm = (key: 'BR' | UfCode, p1: PlaceResult, c: PlaceResult, p2: PlaceResult | null, scope: Scope) => {
    const l1 = leaderOfVotes(...votesOf(p1, pair), pair);
    if (!l1 || leaderOfVotes(...votesOf(c, pair), pair) !== l1) return;
    const last = memo.lastLeader[key];
    memo.lastLeader[key] = l1;
    if (last === undefined || last === l1) return;
    const minFrac = key === 'BR' ? cfg.leadChangeMinFraction : cfg.ufLeadChangeMinFraction;
    if (fracOf(p1) < minFrac || prev1Ht === null) return;
    if (key !== 'BR') {
      const t1 = tMin(prev1Ht, date);
      const lastChange = memo.lastUfChangeTMin[key];
      if (lastChange !== undefined && Math.round((t1 - lastChange) * 60) < cfg.ufLeadChangeCooldownMin * 60) return;
      memo.lastUfChangeTMin[key] = t1;
    }
    const crossing = crossingOf(p2, p1, l1, pair);
    const type = key === 'BR' ? 'national_lead_change' : 'uf_lead_change';
    out.push(
      makeEvent(type, prev1.seq, prev1Ht, detected, scope, key === 'BR' ? 3 : 2, 'official', 'president', {
        type,
        from: last,
        to: l1,
        sections_fraction: round6(fracOf(p1)),
        ...(crossing ? { crossing } : {}),
      }),
    );
  };
  confirm('BR', P1.national, C.national, P2?.national ?? null, { level: 'BR' });
  for (const uf of UF_CODES) {
    const p1 = P1.places[uf];
    const c = C.places[uf];
    if (p1 && c) confirm(uf, p1, c, P2?.places[uf] ?? null, { level: 'UF', code: uf });
  }

  // ── Decisões (presidente: BR; governador: UF) ──
  for (const key of ['math_decision', 'tse_result'] as const) {
    const d = C.national[key];
    if (d && !P1.national[key]) {
      out.push(
        makeEvent(key, d.seq, d.at, detected, { level: 'BR' }, 3, 'official', 'president', {
          type: key,
          winner: d.winner,
        }),
      );
    }
    for (const [uf, race] of Object.entries(curr.races.governor ?? {})) {
      const gd = race?.[key];
      if (gd && !prev1.races.governor?.[uf as UfCode]?.[key]) {
        out.push(
          makeEvent(key, gd.seq, gd.at, detected, { level: 'UF', code: uf }, 2, 'official', 'governor', {
            type: key,
            winner: gd.winner,
          }),
        );
      }
    }
  }

  // ── Correções (contador regride na mesma época, I-08) ──
  if (curr.epoch === prev1.epoch) {
    const correction = (before: PlaceResult, after: PlaceResult, scope: Scope) => {
      const [a0, b0] = votesOf(before, pair);
      const [a1, b1] = votesOf(after, pair);
      let data: ElectionEventData | null = null;
      if (after.sections_counted < before.sections_counted) {
        data = {
          type: 'correction',
          field: 'sections_counted',
          before: before.sections_counted,
          after: after.sections_counted,
        };
      } else if (b1 < b0) data = { type: 'correction', field: 'votes', candidate: pair[1], before: b0, after: b1 };
      else if (a1 < a0) data = { type: 'correction', field: 'votes', candidate: pair[0], before: a0, after: a1 };
      if (data) out.push(makeEvent('correction', seq, occurred, detected, scope, 1, 'ops', 'president', data));
    };
    correction(P1.national, C.national, { level: 'BR' });
    for (const code of PLACE_CODES) {
      const before = P1.places[code];
      const after = C.places[code];
      if (before && after) {
        correction(before, after, code === 'ZZ' ? { level: 'ABROAD', code } : { level: 'UF', code });
      }
    }
  }

  // ── Modelo (transições de status e de faixa entre prev1 e curr; a histerese é do publicador) ──
  modelEvents(prev1.probability, curr.probability, seq, detected, memo, out);

  return { events: out, memo };
}

function modelEvents(
  p0: ProbabilityPayload | null,
  p1: ProbabilityPayload | null,
  seq: number,
  detected: Iso,
  memo: DeriveMemo,
  out: ElectionEvent[],
): void {
  if (!p1) return;
  const s0 = p0?.status ?? null;
  const push = (type: ElectionEvent['type'], data: ElectionEventData) =>
    out.push(makeEvent(type, seq, p1.generated_at, detected, { level: 'BR' }, 2, 'model', 'president', data));
  if (p1.status === 'active' && s0 !== 'active') {
    if (s0 === 'paused' && memo.modelActivated) push('model_resumed', { type: 'model_resumed', reason: null });
    else push('model_activated', { type: 'model_activated', favorite: p1.favorite, band: p1.band ?? 'open_race' });
    memo.modelActivated = true;
  } else if (p1.status === 'active' && s0 === 'active' && p1.band !== p0?.band && p1.band) {
    push('model_band_change', { type: 'model_band_change', favorite: p1.favorite, band: p1.band });
  } else if (p1.status === 'paused' && s0 !== 'paused') {
    push('model_paused', { type: 'model_paused', reason: modelEventReason(p1.public_reason) });
  } else if (p1.status === 'ended' && s0 !== 'ended') {
    push('model_ended', { type: 'model_ended', reason: null });
  }
}

/**
 * Sem `events.json` (falha): `national_lead_change`, `count_milestone` e lotes `sections_batch` retroativos (com
 * `places` vazio, logo sem selos de UF) recalculados sobre a `historySeries` completa, pontos consecutivos como trios
 * (§14.8.8). `nationalSections` (seções do Brasil) converte a fração em seções; sem ele, não há lotes.
 */
export function deriveFromHistory(
  points: readonly HistoryPoint[],
  pair: Pair,
  nationalSections: number | null,
  cfg: EventRulesConfig = EVENT_RULES,
): ElectionEvent[] {
  const out: ElectionEvent[] = [];
  let lastLeader: CandidateNumber | undefined;
  for (let i = 1; i < points.length; i++) {
    const p1 = points[i - 1] as HistoryPoint;
    const c = points[i] as HistoryPoint;
    const p2 = points[i - 2] ?? null;
    if (nationalSections !== null && nationalSections > 0) {
      const sections = Math.round((c.sections_fraction - p1.sections_fraction) * nationalSections);
      if (sections >= 1) {
        out.push(
          makeEvent('sections_batch', c.seq, c.tse_ht, c.tse_ht, { level: 'BR' }, 1, 'official', 'president', {
            type: 'sections_batch',
            sections,
            places: [],
            shares: sharesOfVotes(c.votes[0], c.votes[1]),
            national_sections_fraction: round6(c.sections_fraction),
          }),
        );
      }
    }
    const crossed = cfg.milestones.filter((m) => p1.sections_fraction < m && m <= c.sections_fraction);
    const milestone = crossed[crossed.length - 1];
    if (milestone !== undefined) {
      out.push(
        makeEvent('count_milestone', c.seq, c.tse_ht, c.tse_ht, { level: 'BR' }, 1, 'official', 'president', {
          type: 'count_milestone',
          milestone: milestone as Extract<ElectionEventData, { type: 'count_milestone' }>['milestone'],
          shares: sharesOfVotes(c.votes[0], c.votes[1]),
        }),
      );
    }
    const l1 = leaderOfVotes(p1.votes[0], p1.votes[1], pair);
    if (!l1 || leaderOfVotes(c.votes[0], c.votes[1], pair) !== l1) continue;
    const last = lastLeader;
    lastLeader = l1;
    if (last === undefined || last === l1 || p1.sections_fraction < cfg.leadChangeMinFraction) continue;
    let crossing: { sections_fraction: number; share_to: number } | undefined;
    if (p2 && p2.votes[0] + p2.votes[1] > 0) {
      const k = l1 === pair[0] ? 0 : 1;
      const s2 = sharesOfVotes(p2.votes[0], p2.votes[1])[k];
      const s1 = sharesOfVotes(p1.votes[0], p1.votes[1])[k];
      if (s2 < 0.5 && s1 >= 0.5 && s1 > s2) {
        crossing = {
          sections_fraction: round6(
            p2.sections_fraction + ((0.5 - s2) / (s1 - s2)) * (p1.sections_fraction - p2.sections_fraction),
          ),
          share_to: 0.5,
        };
      }
    }
    out.push(
      makeEvent('national_lead_change', p1.seq, p1.tse_ht, c.tse_ht, { level: 'BR' }, 3, 'official', 'president', {
        type: 'national_lead_change',
        from: last,
        to: l1,
        sections_fraction: round6(p1.sections_fraction),
        ...(crossing ? { crossing } : {}),
      }),
    );
  }
  return out;
}

/** Ordem final (§14.8.8): `occurred_at` decrescente, depois `salience` decrescente, depois `id`. */
export function compareEvents(x: ElectionEvent, y: ElectionEvent): number {
  const tx = Date.parse(x.occurred_at);
  const ty = Date.parse(y.occurred_at);
  if (tx !== ty) return ty - tx;
  if (x.salience !== y.salience) return y.salience - x.salience;
  return x.id < y.id ? -1 : x.id > y.id ? 1 : 0;
}

/**
 * Provisórios × publicados (§14.8.8): com um `events.json` válido de seq de cabeçalho `S`, os provisórios com
 * `seq ≤ S` são trocados pelos publicados; os com `seq > S` ficam. Deduplicação por `id` (o publicado vence).
 */
export function mergeEvents(
  provisional: readonly ElectionEvent[],
  published: readonly ElectionEvent[] | null,
  publishedSeq: number | null,
): ElectionEvent[] {
  const byId = new Map<string, ElectionEvent>();
  if (published) for (const e of published) byId.set(e.id, e);
  for (const e of provisional) {
    if (published && publishedSeq !== null && e.seq <= publishedSeq) continue;
    if (!byId.has(e.id)) byId.set(e.id, e);
  }
  return [...byId.values()].sort(compareEvents);
}

/** Acrescenta eventos a uma lista sem repetir id (o que já está fica). */
export function addEvents(list: readonly ElectionEvent[], more: readonly ElectionEvent[]): ElectionEvent[] {
  if (more.length === 0) return [...list];
  const seen = new Set(list.map((e) => e.id));
  return [...list, ...more.filter((e) => !seen.has(e.id))];
}
